// RFC-4180 CSV export with iOS `CSVExporter` parity.
//
// Usage:
//   downloadCSV("Scans-20260101-1530", ["Email", "Time", "Activity"], rows)
//
// Rows can be:
//  - arrays of strings in the same order as `headers`, or
//  - objects whose keys are the lower-camel of each header (best-effort).
//
// Embedded commas / quotes / newlines are escaped per RFC-4180 — wrap
// the field in double quotes and double any internal quote.

import { headerToKey } from "@/utils/tableHeaders.js";

const escapeField = (value) => {
  if (value == null) return "";
  let str;
  if (value instanceof Date) {
    str = value.toISOString();
  } else if (typeof value === "number") {
    // Treat large numbers as Unix ms timestamps and render as ISO for
    // human-readable CSVs — but only if it looks like a timestamp (post-2000).
    if (value > 9.46e11 && value < 4.1e12) {
      str = new Date(value).toISOString();
    } else {
      str = String(value);
    }
  } else {
    str = String(value);
  }
  if (str.search(/[",\r\n]/) !== -1) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const rowToFields = (row, headers) => {
  if (Array.isArray(row)) {
    return headers.map((_, i) => escapeField(row[i]));
  }
  if (row && typeof row === "object") {
    return headers.map((h) => {
      // Try exact match, then lower-camel.
      if (Object.prototype.hasOwnProperty.call(row, h)) return escapeField(row[h]);
      const camel = headerToKey(h);
      if (Object.prototype.hasOwnProperty.call(row, camel)) return escapeField(row[camel]);
      return "";
    });
  }
  return headers.map(() => "");
};

const buildCsv = (headers, rows) => {
  const lines = [];
  lines.push(headers.map((h) => escapeField(h)).join(","));
  for (const row of rows) {
    lines.push(rowToFields(row, headers).join(","));
  }
  // CRLF line endings per RFC 4180; many parsers accept either, but the
  // spec mandates CRLF.
  return lines.join("\r\n");
};

// Returns "yyyymmdd-hhmm" in local time, matching iOS naming.
export const buildCsvTimestamp = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${y}${m}${d}-${hh}${mm}`;
};

// Sanitise a free-form name into a safe filename slug.
export const slugifyForFilename = (value) => {
  return String(value || "")
    .trim()
    .replace(/[^a-zA-Z0-9-_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 64);
};

// Triggers a download in the browser via a transient <a download>.
// `filename` may omit the .csv extension — it will be appended.
export const downloadCSV = (filename, headers, rows) => {
  if (typeof document === "undefined" || typeof window === "undefined") return;
  const safeHeaders = Array.isArray(headers) ? headers : [];
  const safeRows = Array.isArray(rows) ? rows : [];
  const csv = buildCsv(safeHeaders, safeRows);
  const finalName = filename
    ? filename.endsWith(".csv")
      ? filename
      : `${filename}.csv`
    : `export-${buildCsvTimestamp()}.csv`;

  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = finalName;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Defer revoke so Safari has a chance to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
